import { useEffect, useState, useCallback } from 'react'
import { Coins, Check, Shield, ArrowLeft, CreditCard, Smartphone, Building2, ExternalLink, Loader2, RefreshCw } from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTokenStore } from '../store/tokenStore'
import client from '../api/client'
import type { PaymentPlan } from '../types'
import { getCached, setCache } from '../utils/cache'

export default function Payment() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { balance, starsBalance, fetchBalance } = useTokenStore()
  const [plans, setPlans] = useState<PaymentPlan[]>(() => getCached<PaymentPlan[]>('payment_plans') || [])
  const [isLoading, setIsLoading] = useState(!getCached<PaymentPlan[]>('payment_plans'))
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)
  const [isPaying, setIsPaying] = useState(false)
  const [success, setSuccess] = useState<string | null>(null)
  const [exchangeRate, setExchangeRate] = useState<number | null>(null)

  const checkPendingOrders = useCallback(async () => {
    setSuccess('⏳ Проверяем статус заказов...')
    try {
      const { data } = await client.get('/payment/orders', { params: { limit: 5 } })
      const pending = (data.orders || []).filter(o => o.status === 'waiting_for_capture' && o.yookassa_id)
      let checked = 0
      for (const order of pending) {
        try {
          const res = await client.post('/payment/check-order', { order_id: order.id })
          if (res.data.status === 'succeeded') checked++
        } catch (_) {}
      }
      await fetchBalance()
      if (checked > 0) {
        setSuccess(`✅ ${checked} заказ(ов) подтверждено! Баланс обновлён.`)
      } else if (pending.length > 0) {
        setSuccess('⏳ Платёж ещё обрабатывается. Если деньги списаны — подождите минуту и нажмите кнопку обновления.')
      } else {
        setSuccess('✅ Новых платежей не найдено')
      }
      setTimeout(() => setSuccess(null), 8000)
    } catch (_) {
      setSuccess('❌ Ошибка при проверке статуса')
      setTimeout(() => setSuccess(null), 4000)
    }
  }, [fetchBalance])

  // Обработка возврата из ЮKassa — автоматом проверяем статус
  useEffect(() => {
    if (searchParams.get('success') === '1') {
      setSuccess('⏳ Проверяем статус платежа...')
      window.history.replaceState({}, '', '/payment')
      setTimeout(checkPendingOrders, 3000)
    }
  }, [searchParams, checkPendingOrders])

  useEffect(() => {
    fetchBalance()
    client.get('/tokens/plans').then(({ data }) => {
      const plans = data.plans ?? []
      setPlans(plans)
      setCache('payment_plans', plans)
      setIsLoading(false)
    }).catch(() => setIsLoading(false))
    client.get('/admin/ai-models/usd-rate').then(({ data }) => {
      setExchangeRate(data.rate)
    }).catch(() => {})
  }, [fetchBalance])

  const refreshBalance = useCallback(async () => {
    setSuccess('⏳ Проверяем баланс...')
    await fetchBalance()
    setSuccess('✅ Баланс обновлён!')
    setTimeout(() => setSuccess(null), 4000)
  }, [fetchBalance])

  const handlePurchase = async (plan: PaymentPlan) => {
    setSelectedPlan(plan.id)
    setIsPaying(true)
    setSuccess(null)
    try {
      const { data } = await client.post('/payment/create-order', { plan_id: plan.id })
      if (data.payment_url) {
        window.open(data.payment_url, '_blank')
        setSuccess('💳 Платёжная страница открыта в новой вкладке')
      } else {
        setSuccess('Платёжный шлюз настраивается. Заказ #' + data.order_id.slice(0, 8) + ' создан')
      }
      await fetchBalance()
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Ошибка при создании заказа'
      setSuccess(msg)
    } finally {
      setIsPaying(false)
      setTimeout(() => setSuccess(null), 6000)
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <button
          onClick={() => navigate('/profile')}
          className="p-2 rounded-xl glass-hover text-gray-400 hover:text-white transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold gradient-text flex items-center gap-2">
            <Coins className="w-6 h-6" />
            Пополнение баланса
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Текущий баланс: <span className="text-amber-300 font-semibold">{(balance ?? 0).toLocaleString()}</span> CN / ✦{starsBalance.toLocaleString()} звёзд
          </p>
        </div>
      </div>

      {/* Success toast + manual refresh */}
      {success && (
        <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-300 text-sm flex items-center gap-3">
          <Check className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button
            onClick={checkPendingOrders}
            className="p-1.5 rounded-lg hover:bg-green-500/10 transition-colors"
            title="Проверить статус платежа"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Plans grid */}
      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Бесплатный тариф — отдельная карточка */}
          {plans.filter(p => (p as any).free).map((plan) => {
            return (
              <div
                key={plan.id}
                className="glass-card p-5 transition-all duration-200 col-span-full sm:col-span-2 lg:col-span-1 border border-green-500/20 bg-green-900/10"
              >
                <div className="text-center pt-1">
                  <h2 className="text-2xl font-bold text-white">{plan.label}</h2>
                  <div className="mt-4 mb-4">
                    <p className="text-lg font-bold text-green-400">Безлимитные</p>
                    <p className="text-xs text-gray-500">бесплатные модели</p>
                  </div>
                  <div className="mb-3">
                    <p className="text-3xl font-bold text-white">0₽</p>
                  </div>
                  <div className="text-xs text-gray-500 space-y-1 mb-4">
                    <p>GPT-5.2, GPT-5.1</p>
                    <p>Claude Sonnet 4, Gemini 2.5 Pro</p>
                    <p>Grok 4, DeepSeek V4</p>
                  </div>
                  <div className="w-full py-2.5 rounded-xl bg-gray-700/50 text-gray-400 text-sm font-semibold text-center">
                    Бесплатно
                  </div>
                </div>
              </div>
            )
          })}

          {/* Платные тарифы */}
          {plans.filter(p => !(p as any).free).map((plan) => {
            const tokensPerRub = (plan.tokens / plan.price).toFixed(0)
            return (
              <div
                key={plan.id}
                className={`relative glass-card p-5 transition-all duration-200 ${
                  plan.popular
                    ? 'ring-2 ring-amber-500/50 shadow-lg shadow-amber-500/10'
                    : ''
                } ${selectedPlan === plan.id && isPaying ? 'opacity-70' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-0.5 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold shadow-lg">
                      Популярный
                    </span>
                  </div>
                )}

                <div className="text-center pt-1">
                  <h2 className="text-2xl font-bold text-white">{plan.label}</h2>
                  {plan.id !== 'promax' && (
                    <p className="text-[10px] text-gray-500 mt-0.5">Разовое пополнение</p>
                  )}
                  {plan.id === 'promax' && (
                    <p className="text-[10px] text-sky-400 mt-0.5">🔄 Ежемесячная подписка</p>
                  )}
                  <div className="mt-4 mb-4">
                    <p className="text-2xl font-bold text-amber-300">{plan.tokens.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">CN-токенов</p>
                    <p className="text-sm text-indigo-300 font-semibold mt-1">
                      или ✦ {Math.floor(plan.tokens / 10000).toLocaleString()} звёзд
                      <span className="text-[10px] text-indigo-400/70 block mt-0.5">(для генерации картинок/фото, видео и музыки)</span>
                    </p>
                  </div>
                  <div className="mb-4">
                    <p className="text-3xl font-bold text-white">{plan.price_label}</p>
                    <p className="text-xs text-gray-500">За 1₽ покупаете {tokensPerRub} токенов</p>
                  </div>

                  <button
                    onClick={() => handlePurchase(plan)}
                    disabled={isPaying}
                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {isPaying && selectedPlan === plan.id ? 'Оплата...' : 'Пополнить'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Features */}
      <div className="glass-card p-6">
        <h3 className="text-sm font-semibold text-gray-200 mb-4">Почему выбирают нас</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { icon: Shield, label: 'Безопасная оплата', desc: 'Все платежи защищены' },
            { icon: CreditCard, label: 'Мгновенное зачисление', desc: 'Токены начисляются сразу' },
            { icon: Smartphone, label: 'Доступно 24/7', desc: 'Пополняйте в любое время' },
          ].map((f) => (
            <div key={f.label} className="flex items-start gap-3 p-3 rounded-xl glass-hover">
              <f.icon className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-200">{f.label}</p>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-[10px] text-gray-600 text-center max-w-md mx-auto">
          1 000 000 CN ≈ <span className="text-gray-400">{exchangeRate ? `${exchangeRate.toFixed(2)} ₽` : '.. ₽'}</span>
          · 1 ⭐ ≈ <span className="text-gray-400">{exchangeRate ? `${(exchangeRate / 100).toFixed(2)} ₽` : '.. ₽'}</span>
          · 1 ⭐ = 10 000 CN
          · Минимальная генерация — 1 ⭐ (каждый 10 000 CN — 1 ⭐).
          Курс ЦБ РФ. Подробнее — в <a href="/profile" className="underline text-indigo-400 hover:text-indigo-300">личном кабинете</a>.
        </p>
      </div>
    </div>
  )
}
